import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useComments } from '@/hooks/useComments';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ReportButton } from '@/components/ReportButton';
import { useAuth } from '@/hooks/useAuth';
import { useSuspensionCheck } from '@/hooks/useSuspensionCheck';
import { SuspensionBanner } from '@/components/SuspensionBanner';
import { MentionAutocomplete } from '@/components/MentionAutocomplete';
import { MentionText } from '@/components/MentionText';

interface CommentThreadProps {
  isOpen: boolean;
  onClose: () => void;
  reviewId: string;
  bookTitle: string;
}

export function CommentThread({ isOpen, onClose, reviewId, bookTitle }: CommentThreadProps) {
  const [newComment, setNewComment] = useState('');
  const { comments, loading, submitting, addComment } = useComments(reviewId);
  const { toast } = useToast();
  const { user } = useAuth();
  const { suspensionInfo, loading: suspensionLoading } = useSuspensionCheck();

  const handleSubmit = async () => {
    if (!newComment.trim() || suspensionInfo.isSuspended) return;

    try {
      // Moderate comment before posting
      const { data: moderationResult, error: moderationError } = await supabase.functions.invoke('moderate-content', {
        body: { text: newComment.trim() }
      });

      if (!moderationError && moderationResult?.flagged) {
        toast({
          variant: "destructive",
          title: "Comment contains inappropriate content",
          description: "Your comment violates our guidelines. Please revise and try again.",
        });
        return;
      }

      await addComment(newComment);
      setNewComment('');
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not post comment. Please try again.",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Comments on "{bookTitle}"</span>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col gap-4">
          <ScrollArea className="flex-1 max-h-[400px]">
            <div className="space-y-4 pr-4">
              {loading ? (
                <div className="text-center text-muted-foreground">Loading comments...</div>
              ) : comments.length === 0 ? (
                <div className="text-center text-muted-foreground">
                  No comments yet. Be the first to comment!
                </div>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="flex gap-3">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={comment.profiles?.image || ''} />
                      <AvatarFallback>
                        {comment.profiles?.name?.[0] || comment.profiles?.username?.[0] || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">
                            {comment.profiles?.name || comment.profiles?.username}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        {user?.id !== comment.user_id && (
                          <ReportButton
                            contentType="review_comment"
                            contentId={comment.id}
                            contentPreview={comment.comment_text}
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                          />
                        )}
                      </div>
                      <MentionText text={comment.comment_text} className="text-sm text-foreground" />
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>

          <div className="border-t pt-4">
            <div className="flex flex-col gap-2">
              <MentionAutocomplete
                value={newComment}
                onChange={setNewComment}
              >
                <Textarea
                  placeholder="Write a comment..."
                  rows={3}
                  className="resize-none"
                />
              </MentionAutocomplete>
              <div className="flex justify-end">
                <Button 
                  onClick={handleSubmit}
                  disabled={!newComment.trim() || submitting}
                  size="sm"
                >
                  {submitting ? 'Posting...' : 'Post Comment'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}